import { useState } from 'react';
import {
  makeStyles,
  tokens,
  Button,
  Spinner,
  Body1,
  Body2,
  Caption1,
} from '@fluentui/react-components';
import { useAuth } from '@/hooks/useAuth';
import { transformrBrand } from '@/theme/transformrTheme';

// Local alias map keeps the JSX readable. All hex values live in transformrTheme.
const brandColors = {
  green: transformrBrand.greenAlt,
  darkGreen: transformrBrand.greenDeep,
  lightGreen: transformrBrand.greenPale,
  blue: transformrBrand.blue,
  white: transformrBrand.white,
  darkGrey: transformrBrand.textDark,
};

const useStyles = makeStyles({
  page: {
    display: 'flex',
    minHeight: '100vh',
  },
  brandPanel: {
    flex: '1 1 55%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '64px 48px',
    background: `linear-gradient(135deg, ${brandColors.darkGreen} 0%, ${brandColors.green} 60%, ${brandColors.lightGreen} 100%)`,
    color: brandColors.white,
    position: 'relative',
    overflow: 'hidden',
    '@media (max-width: 768px)': {
      display: 'none',
    },
  },
  brandContent: {
    position: 'relative',
    zIndex: 1,
    maxWidth: '480px',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalXXL,
  },
  brandLogo: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalL,
    marginBottom: tokens.spacingVerticalL,
  },
  brandTitle: {
    fontSize: tokens.fontSizeHero1000,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightHero1000,
    margin: 0,
  },
  brandSubtitle: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightRegular,
    opacity: 0.9,
    margin: 0,
  },
  brandTagline: {
    fontSize: tokens.fontSizeBase400,
    lineHeight: tokens.lineHeightBase400,
    opacity: 0.8,
    margin: 0,
  },
  brandFeatures: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    marginTop: tokens.spacingVerticalL,
  },
  feature: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    fontSize: tokens.fontSizeBase300,
    opacity: 0.9,
  },
  featureDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: brandColors.blue,
    flexShrink: 0,
  },
  bgCircle1: {
    position: 'absolute',
    width: '500px',
    height: '500px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255,255,255,0.04)',
    top: '-100px',
    right: '-150px',
  },
  bgCircle2: {
    position: 'absolute',
    width: '300px',
    height: '300px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255,255,255,0.03)',
    bottom: '-50px',
    left: '-80px',
  },
  loginPanel: {
    flex: '1 1 45%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '48px 40px',
    backgroundColor: brandColors.white,
    '@media (max-width: 768px)': {
      flex: '1 1 100%',
    },
  },
  loginCard: {
    maxWidth: '380px',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalXXXL,
  },
  loginHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalM,
  },
  welcomeText: {
    fontSize: tokens.fontSizeHero800,
    fontWeight: tokens.fontWeightSemibold,
    color: brandColors.darkGrey,
    margin: 0,
  },
  loginBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalXL,
  },
  description: {
    color: tokens.colorNeutralForeground2,
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
    padding: '12px 16px',
    backgroundColor: tokens.colorPaletteRedBackground1,
    borderRadius: tokens.borderRadiusMedium,
  },
  signInButton: {
    width: '100%',
    height: '44px',
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
  },
  footer: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalS,
    textAlign: 'center',
  },
});

export function LoginPage() {
  const styles = useStyles();
  const { loginRedirect, isLoading } = useAuth();
  const [error, setError] = useState<string | null>(null);

  const handleLogin = async () => {
    try {
      setError(null);
      await loginRedirect();
    } catch (err) {
      setError('Sign in failed. Please try again.');
      console.error('Login error:', err);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.brandPanel}>
        <div className={styles.bgCircle1} />
        <div className={styles.bgCircle2} />
        <div className={styles.brandContent}>
          <div className={styles.brandLogo}>
            <img
              src="/assets/logos/transformr-logo.png"
              alt="Transformr"
              style={{ height: '56px', objectFit: 'contain', filter: 'brightness(0) invert(1)' }}
            />
          </div>
          <h1 className={styles.brandTitle}>
            Transformr AI Readiness
          </h1>
          <p className={styles.brandSubtitle}>
            Find out where your organisation actually stands with AI.
          </p>
          <p className={styles.brandTagline}>
            Get a personalised assessment of your AI readiness in minutes — based on
            your industry, your role, and how organisations like yours are deploying
            AI right now.
          </p>
          <div className={styles.brandFeatures}>
            <div className={styles.feature}>
              <div className={styles.featureDot} />
              <span>A report tailored to your industry and compliance context</span>
            </div>
            <div className={styles.feature}>
              <div className={styles.featureDot} />
              <span>Use cases already in production for organisations like yours</span>
            </div>
            <div className={styles.feature}>
              <div className={styles.featureDot} />
              <span>The common blockers that stall AI initiatives in your sector</span>
            </div>
            <div className={styles.feature}>
              <div className={styles.featureDot} />
              <span>Your next three steps, in the right order</span>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.loginPanel}>
        <div className={styles.loginCard}>
          <div className={styles.loginHeader}>
            <h2 className={styles.welcomeText}>Get started</h2>
            <Body1 className={styles.description}>
              Sign in with your Microsoft 365 account to receive your personalised AI Readiness report.
            </Body1>
          </div>

          <div className={styles.loginBody}>
            {error && (
              <Body2 className={styles.error}>{error}</Body2>
            )}

            <Button
              appearance="primary"
              size="large"
              className={styles.signInButton}
              onClick={handleLogin}
              disabled={isLoading}
              icon={isLoading ? <Spinner size="tiny" /> : undefined}
            >
              {isLoading ? 'Signing in...' : 'Sign in with Microsoft 365'}
            </Button>
          </div>

          <div className={styles.footer}>
            <Caption1 className={styles.description}>
              Sign in with your work or school Microsoft 365 account to get started.
            </Caption1>
          </div>
        </div>
      </div>
    </div>
  );
}
